import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { useVirtualizer } from '@tanstack/react-virtual'
import { FunnelIcon } from '@heroicons/react/20/solid'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DASHBOARD_COLUMNS, FIXED_COLUMNS } from './columns'
import type { DashboardColumn } from './columns'
import {
  readColumnOrder, writeColumnOrder,
  readColumnWidths, writeColumnWidths,
  readFilters, writeFilters,
  type FilterRecord,
} from './dashboardStorage'
import { renderCell, computeSummary } from './formatters'
import { FormSelect } from '@/components/ui/form'
import type { CellProps } from './formatters'

const ROW_HEIGHT = 28

// ─── Types ──────────────────────────────────────

type Row = Record<string, unknown>
type SortDir = 'asc' | 'desc' | null
interface SortState { column: string; direction: SortDir }
// `FilterRecord` from dashboardStorage is the same shape — re-alias so
// the rest of this file (which references FilterState) keeps compiling
// without an editor-wide rename.
type FilterState = FilterRecord

interface DashboardTableProps {
  data: Row[]
  users: { label: string; value: string }[]
  csrfToken: string
  onNotify: (msg: string) => void
  onFetchColumn: (columnName: string) => Promise<{ id: number; [key: string]: unknown }[]>
  // Select options from backend
  statesOptions: { label: string; value: string }[]
  subStateNames: Record<string, Record<string, string>>
  subStateComments: Record<string, Record<string, string>>
  probabilitiesOptions: { label: string; value: string }[]
  financialStagesOptions: { label: string; value: string }[]
  currentUserRole: string
  // Column state (lifted to parent for Toolbar integration)
  hiddenColumns: Set<string>
  onHiddenColumnsChange: (hidden: Set<string>) => void
  onSelectionChange?: (selectedIds: Set<number>) => void
}

// ─── localStorage helpers ───────────────────────

function lsGet<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v ? JSON.parse(v) : fallback
  } catch { return fallback }
}
function lsSet(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* noop */ }
}

// ─── Component ──────────────────────────────────

export default function DashboardTable({
  data, users, csrfToken, onNotify, onFetchColumn: _onFetchColumn,
  statesOptions, subStateNames, subStateComments,
  probabilitiesOptions, financialStagesOptions, currentUserRole,
  hiddenColumns, onHiddenColumnsChange: _onHiddenColumnsChange,
  onSelectionChange,
}: DashboardTableProps) {
  // ─── State ──────────────────────────────────
  const [rows, setRows] = useState<Row[]>(data)
  const [sort, setSort] = useState<SortState>(() => lsGet('v2-dashboard-sorting', { column: '', direction: null }))
  // Filters live in the legacy `filters` LS key as an array of
  // `{columnName, value:[...]}`. dashboardStorage.readFilters converts
  // that to the record-of-Set shape the React code wants; writeFilters
  // converts back so the legacy app keeps seeing its expected shape.
  const [filters, setFilters] = useState<Record<string, FilterRecord>>(() => readFilters())
  // Column order lives in the legacy `columnOrder` key — same array
  // shape on both sides, no conversion needed.
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    readColumnOrder(DASHBOARD_COLUMNS.map(c => c.name))
  )
  // Column widths in legacy `columnWidths` key — array
  // `[{columnName, width}]` on disk, record `{col: width}` in React
  // state. dashboardStorage handles the conversion in both directions
  // and merges with defaults on the read side.
  const COLUMN_WIDTH_DEFAULTS = useMemo(() => {
    const d: Record<string, number> = {}
    DASHBOARD_COLUMNS.forEach(c => { d[c.name] = c.width })
    return d
  }, [])
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    readColumnWidths(COLUMN_WIDTH_DEFAULTS)
  )
  const [selection, setSelection] = useState<Set<number>>(new Set())
  // activeFilter carries the col name + its anchor screen position. We
  // capture the position synchronously at click time (from the button's
  // own currentTarget) instead of reading it from a ref during the
  // popover's render — the ref can be momentarily null on re-render,
  // which is what previously made the panel flash at viewport (0,0)
  // near the logo.
  const [activeFilter, setActiveFilter] = useState<{ col: string; top: number; left: number } | null>(null)

  // Date-range filters live in their own bucket. Each entry is
  // `{ from?: 'YYYY-MM-DD', to?: 'YYYY-MM-DD' }`. Filter applies when at
  // least one bound is set: rows whose date column value falls inside
  // [from, to] (either bound inclusive) stay; the rest drop. Persisted
  // separately from the set-style `filters` so the two shapes don't mix.
  const [dateRangeFilters, setDateRangeFilters] = useState<Record<string, { from?: string; to?: string }>>(() =>
    lsGet('v2-dashboard-date-range-filters', {})
  )
  useEffect(() => { lsSet('v2-dashboard-date-range-filters', dateRangeFilters) }, [dateRangeFilters])
  const setDateRange = useCallback((colName: string, next: { from?: string; to?: string } | null) => {
    setDateRangeFilters(prev => {
      const copy = { ...prev }
      if (!next || (!next.from && !next.to)) delete copy[colName]
      else copy[colName] = next
      return copy
    })
  }, [])
  const [editingCell, setEditingCell] = useState<{ rowId: number; colName: string } | null>(null)
  const [flashRow, setFlashRow] = useState<{ id: number; status: 'success' | 'error' } | null>(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onFlash = useCallback((rowId: number, status: 'success' | 'error') => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    setFlashRow({ id: rowId, status })
    flashTimerRef.current = setTimeout(() => setFlashRow(null), 700)
  }, [])

  const tableRef = useRef<HTMLDivElement>(null)
  const resizingRef = useRef<{ col: string; startX: number; startW: number } | null>(null)

  // Sync data from parent
  useEffect(() => { setRows(data) }, [data])

  // Persist state — v2-only key for sort (legacy didn't persist it),
  // legacy-shape keys for everything else (via dashboardStorage so the
  // legacy app keeps reading the right format).
  useEffect(() => { lsSet('v2-dashboard-sorting', sort) }, [sort])
  useEffect(() => { writeFilters(filters) }, [filters])
  useEffect(() => { writeColumnOrder(columnOrder) }, [columnOrder])
  useEffect(() => { writeColumnWidths(columnWidths, COLUMN_WIDTH_DEFAULTS) }, [columnWidths, COLUMN_WIDTH_DEFAULTS])

  // ─── Column map ─────────────────────────────
  const columnMap = useMemo(() => {
    const m = new Map<string, DashboardColumn>()
    DASHBOARD_COLUMNS.forEach(c => m.set(c.name, c))
    return m
  }, [])

  // ─── Visible columns (respecting order) ─────
  const visibleColumns = useMemo(() => {
    return columnOrder
      .filter(name => !hiddenColumns.has(name))
      .map(name => columnMap.get(name))
      .filter(Boolean) as DashboardColumn[]
  }, [columnOrder, hiddenColumns, columnMap])

  // ─── Fixed columns ──────────────────────────
  const fixedCols = useMemo(() => visibleColumns.filter(c => FIXED_COLUMNS.includes(c.name)), [visibleColumns])
  const scrollCols = useMemo(() => visibleColumns.filter(c => !FIXED_COLUMNS.includes(c.name)), [visibleColumns])
  const checkboxW = 40
  const totalWidth = useMemo(() =>
    checkboxW + visibleColumns.reduce((s, c) => s + (columnWidths[c.name] || c.width), 0),
    [visibleColumns, columnWidths]
  )

  // ─── Sorting ────────────────────────────────
  const handleSort = useCallback((colName: string) => {
    setSort(prev => {
      if (prev.column !== colName) return { column: colName, direction: 'asc' }
      if (prev.direction === 'asc') return { column: colName, direction: 'desc' }
      return { column: '', direction: null }
    })
  }, [])

  // ─── Filtering ──────────────────────────────
  const toggleFilter = useCallback((colName: string, value: string) => {
    setFilters(prev => {
      const existing = prev[colName]
      const values = existing ? new Set(existing.values) : new Set<string>()
      if (values.has(value)) values.delete(value)
      else values.add(value)
      if (values.size === 0) {
        const next = { ...prev }
        delete next[colName]
        return next
      }
      return { ...prev, [colName]: { column: colName, values } }
    })
  }, [])

  const clearFilter = useCallback((colName: string) => {
    setFilters(prev => {
      const next = { ...prev }
      delete next[colName]
      return next
    })
  }, [])

  // ─── Processed rows (sort + filter) ─────────
  const processedRows = useMemo(() => {
    let result = [...rows]

    // Apply set-style filters
    Object.values(filters).forEach(f => {
      if (f.values.size > 0) {
        result = result.filter(r => f.values.has(String(r[f.column] ?? '')))
      }
    })

    // Apply date-range filters. Date columns serialize as 'mm/dd/yyyy'
    // strings — parse both bounds + cell through Date.parse for a lenient
    // comparison. Either bound may be absent (open-ended range).
    Object.entries(dateRangeFilters).forEach(([colName, range]) => {
      const fromTs = range.from ? Date.parse(range.from) : null
      const toTs   = range.to   ? Date.parse(range.to)   : null
      if (fromTs == null && toTs == null) return
      result = result.filter(r => {
        const raw = r[colName]
        if (raw == null || raw === '') return false
        const v = Date.parse(String(raw))
        if (Number.isNaN(v)) return false
        if (fromTs != null && v < fromTs) return false
        if (toTs   != null && v > toTs)   return false
        return true
      })
    })

    // Apply sort
    if (sort.column && sort.direction) {
      const dir = sort.direction === 'asc' ? 1 : -1
      result.sort((a, b) => {
        const va = a[sort.column], vb = b[sort.column]
        if (va == null && vb == null) return 0
        if (va == null) return 1
        if (vb == null) return -1
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        return String(va).localeCompare(String(vb)) * dir
      })
    }

    return result
  }, [rows, filters, dateRangeFilters, sort])

  // ─── Selection ──────────────────────────────
  const allSelected = processedRows.length > 0 && processedRows.every(r => selection.has(r.id as number))
  const toggleAll = useCallback(() => {
    const next = allSelected ? new Set<number>() : new Set(processedRows.map(r => r.id as number))
    setSelection(next)
    onSelectionChange?.(next)
  }, [allSelected, processedRows, onSelectionChange])
  const toggleRow = useCallback((id: number) => {
    setSelection(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      onSelectionChange?.(next)
      return next
    })
  }, [onSelectionChange])

  // ─── Cell update ────────────────────────────
  const onCellUpdate = useCallback((rowId: number, updates: { col: string; val: unknown }[]) => {
    setRows(prev => prev.map(r => {
      if ((r.id as number) !== rowId) return r
      const updated = { ...r }
      updates.forEach(u => { updated[u.col] = u.val })
      return updated
    }))
  }, [])

  // ─── Column reveal (lazy load) — exposed via ref or called from parent
  // When parent reveals a column, it should call onFetchColumn if data is missing
  // This is handled in Index.tsx handleHiddenColumnsChange

  // Floor — keep it small so unused columns can be shrunk down. The
  // header label will overflow with text-overflow:clip if needed.
  const getMinWidth = useCallback((_colName: string) => 30, [])

  // ─── Column resize ──────────────────────────
  const startResize = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault()
    resizingRef.current = { col, startX: e.clientX, startW: columnWidths[col] || 100 }
    const minW = getMinWidth(col)
    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return
      const { col: resizeCol, startX, startW } = resizingRef.current
      const diff = ev.clientX - startX
      const newW = Math.max(minW, startW + diff)
      setColumnWidths(prev => ({ ...prev, [resizeCol]: newW }))
    }
    const onUp = () => {
      resizingRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [columnWidths])

  // ─── Unique values for filter dropdowns ─────
  const getUniqueValues = useCallback((colName: string): string[] => {
    const vals = new Set<string>()
    rows.forEach(r => { if (r[colName] != null) vals.add(String(r[colName])) })
    return [...vals].sort()
  }, [rows])

  // ─── Virtualizer ─────────────────────────────
  const rowVirtualizer = useVirtualizer({
    count: processedRows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
  })

  // ─── Filter button refs for portal positioning ─
  const filterBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  // ─── Drag-and-drop column reordering ────────
  // PointerSensor with a small activation distance lets the existing
  // sort/filter click handlers on the header keep working — drag only
  // engages after the pointer actually moves a few px.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setColumnOrder(prev => {
      const oldIdx = prev.indexOf(String(active.id))
      const newIdx = prev.indexOf(String(over.id))
      if (oldIdx < 0 || newIdx < 0) return prev
      return arrayMove(prev, oldIdx, newIdx)
    })
  }, [])

  // ─── Render header cell (matches ProductsTable style) ─
  // Fixed columns render as a plain <th>. Scroll columns are wrapped by
  // useSortable so the user can drag them to reorder; the drag handle is
  // the column-title span (not the filter button, not the resize handle).
  function HeaderCell({ col, isFixed }: { col: DashboardColumn; isFixed: boolean }) {
    const w = columnWidths[col.name] || col.width
    const mw = getMinWidth(col.name)
    const isDate = col.type === 'date'
    const dr = dateRangeFilters[col.name]
    const hasDateRange = !!dr && (!!dr.from || !!dr.to)
    const hasFilter = isDate
      ? hasDateRange
      : !!filters[col.name] && filters[col.name].values.size > 0
    const filterCountBadge = isDate
      ? hasDateRange ? 1 : 0
      : filters[col.name]?.values.size || 0

    // useSortable is a hook — always invoke it, then ignore its output for
    // fixed columns. Conditional hook calls would violate the rules of
    // hooks.
    const sortable = useSortable({ id: col.name, disabled: isFixed })
    const dndStyle = isFixed
      ? undefined
      : {
          transform: CSS.Transform.toString(sortable.transform),
          transition: sortable.transition,
          opacity: sortable.isDragging ? 0.5 : 1,
        }

    return (
      <th
        ref={isFixed ? undefined : sortable.setNodeRef}
        key={col.name}
        data-testid={`col-th-${col.name}`}
        className={clsx(
          'relative select-none whitespace-nowrap py-2 pl-2 pr-4',
          isFixed ? 'sticky z-20 bg-white dark:bg-zinc-900' : 'border-r border-zinc-200 dark:border-zinc-700',
          !isFixed && sortable.isDragging && 'z-30',
        )}
        style={{
          width: w,
          minWidth: mw,
          maxWidth: w,
          left: isFixed ? getFixedLeft(col.name) : undefined,
          ...dndStyle,
        }}
      >
        {/* min-w-0 + max-w-full lets the title span shrink and ellipsize
            when the column is narrowed below the title's intrinsic width.
            Without this the header text spills over the next column. */}
        <span className="flex min-w-0 max-w-full items-center gap-0.5">
          <span
            data-testid={`col-header-${col.name}`}
            className={clsx('min-w-0 cursor-pointer truncate', !isFixed && 'touch-none')}
            onClick={() => handleSort(col.name)}
            {...(isFixed ? {} : sortable.attributes)}
            {...(isFixed ? {} : sortable.listeners)}
          >
            {col.title}
            {sort.column === col.name && (
              <span className="ml-0.5 text-indigo-500">{sort.direction === 'asc' ? '↑' : '↓'}</span>
            )}
          </span>
          {/* Filter icon — FunnelIcon like ProductsTable */}
          <button
            data-testid={`col-filter-btn-${col.name}`}
            ref={el => { filterBtnRefs.current[col.name] = el }}
            onClick={(e) => {
              if (activeFilter?.col === col.name) {
                setActiveFilter(null)
                return
              }
              const rect = e.currentTarget.getBoundingClientRect()
              setActiveFilter({ col: col.name, top: rect.bottom + 2, left: rect.left })
            }}
            className={clsx(
              'ml-1 inline-flex shrink-0 items-center gap-0.5 rounded p-0.5',
              hasFilter
                ? 'text-indigo-600 dark:text-indigo-400'
                : 'text-zinc-300 hover:text-zinc-500 dark:text-zinc-600 dark:hover:text-zinc-400'
            )}
            title="Filter"
          >
            <FunnelIcon className="h-3 w-3" />
            {hasFilter && <span className="text-[9px] font-semibold">{filterCountBadge}</span>}
          </button>
        </span>
        {/* Resize handle */}
        <div
          className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50"
          onMouseDown={e => startResize(col.name, e)}
        />
        {/* Sticky column divider — real DOM element, borders don't work with sticky */}
        {isFixed && <div className="absolute inset-y-0 right-0 w-px bg-zinc-200 dark:bg-zinc-700" />}
        {/* Filter popover — date columns get a range picker, everything
            else gets the checkbox list. Same portal positioning logic. */}
        {activeFilter?.col === col.name && (
          isDate ? (
            <DateRangeFilter
              colName={col.name}
              value={dr || {}}
              onChange={(next) => setDateRange(col.name, next)}
              onClear={() => setDateRange(col.name, null)}
              onClose={() => setActiveFilter(null)}
              anchorButton={filterBtnRefs.current[col.name]}
              initialTop={activeFilter.top}
              initialLeft={activeFilter.left}
            />
          ) : (
            <FilterDropdown
              colName={col.name}
              values={getUniqueValues(col.name)}
              selected={filters[col.name]?.values || new Set()}
              onToggle={toggleFilter}
              onClear={() => clearFilter(col.name)}
              onClose={() => setActiveFilter(null)}
              anchorButton={filterBtnRefs.current[col.name]}
              initialTop={activeFilter.top}
              initialLeft={activeFilter.left}
            />
          )
        )}
      </th>
    )
  }

  const headerCell = (col: DashboardColumn, isFixed: boolean) => (
    <HeaderCell key={col.name} col={col} isFixed={isFixed} />
  )

  // ─── Fixed column left offset ───────────────
  function getFixedLeft(colName: string): number {
    let left = 40 // checkbox width
    for (const fc of fixedCols) {
      if (fc.name === colName) return left
      left += columnWidths[fc.name] || fc.width
    }
    return left
  }

  // ─── Cell props builder ─────────────────────
  function cellProps(row: Row, col: DashboardColumn): CellProps {
    return {
      value: row[col.name],
      row,
      column: col,
      csrfToken,
      onCellUpdate,
      onNotify,
      onFlash,
      statesOptions,
      subStateNames,
      subStateComments,
      probabilitiesOptions,
      financialStagesOptions,
      usersList: users,
      currentUserRole,
    }
  }

  // ─── Render ─────────────────────────────────
  return (
    <div ref={tableRef} data-testid="dashboard-table" className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto">
      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <table className="table-fixed text-left text-xs text-zinc-900 dark:text-zinc-100" style={{ width: totalWidth }}>
        <colgroup><col style={{ width: 40 }} />{fixedCols.map(c => <col key={c.name} style={{ width: columnWidths[c.name] || c.width }} />)}{scrollCols.map(c => <col key={c.name} style={{ width: columnWidths[c.name] || c.width }} />)}</colgroup>

        {/* Header */}
        <thead className="sticky top-0 z-30 bg-white dark:bg-zinc-900">
          <tr className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            <th className="relative sticky left-0 z-20 w-10 bg-white px-2 py-2 dark:bg-zinc-900">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="h-3 w-3 rounded border-zinc-300 text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800"
              />
              <div className="absolute inset-y-0 right-0 w-px bg-zinc-200 dark:bg-zinc-700" />
            </th>
            {fixedCols.map(c => headerCell(c, true))}
            <SortableContext
              items={scrollCols.map(c => c.name)}
              strategy={horizontalListSortingStrategy}
            >
              {scrollCols.map(c => headerCell(c, false))}
            </SortableContext>
          </tr>
          <tr aria-hidden="true" style={{ height: 0, lineHeight: 0 }}>
            <td colSpan={visibleColumns.length + 1} className="bg-zinc-200 p-0 dark:bg-zinc-700" style={{ height: 1 }} />
          </tr>
        </thead>

        {/* Body — virtualized */}
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {(() => {
            const virtualItems = rowVirtualizer.getVirtualItems()
            const totalHeight = rowVirtualizer.getTotalSize()
            const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
            const paddingBottom = virtualItems.length > 0 ? totalHeight - virtualItems[virtualItems.length - 1].end : 0
            return (
              <>
                {paddingTop > 0 && <tr><td colSpan={visibleColumns.length + 1} style={{ height: paddingTop, padding: 0, border: 'none' }} /></tr>}
                {virtualItems.map(virtualRow => {
                  const row = processedRows[virtualRow.index]
                  const id = row.id as number
                  const selected = selection.has(id)
                  const isEditing = editingCell?.rowId === id
                  const flash = flashRow?.id === id ? flashRow.status : null
                  // Sticky cells need explicit bg (they can't inherit from tr)
                  const stickyBg = flash === 'success' ? 'bg-emerald-100 dark:bg-emerald-500/15'
                    : flash === 'error' ? 'bg-red-100 dark:bg-red-500/15'
                    : selected ? 'bg-indigo-50 dark:bg-indigo-500/5'
                    : 'bg-white dark:bg-zinc-900'
                  return (
                    <tr
                      key={id}
                      className={clsx(
                        'group transition-colors duration-700',
                        flash === 'success' && 'bg-emerald-100 dark:bg-emerald-500/15',
                        flash === 'error' && 'bg-red-100 dark:bg-red-500/15',
                        !flash && selected && 'bg-indigo-50 dark:bg-indigo-500/5',
                        !flash && !selected && 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                      )}
                    >
                      <td className={clsx('relative sticky left-0 z-10 whitespace-nowrap py-1 pl-2 pr-2 transition-colors duration-700', stickyBg)}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(id)}
                          className="h-3 w-3 rounded border-zinc-300 text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800"
                        />
                        <div className="absolute inset-y-0 right-0 w-px bg-zinc-200 dark:bg-zinc-700" />
                      </td>
                      {fixedCols.map(col => {
                        const isSelect = col.type === 'state' || col.type === 'select' || col.type === 'user_select'
                        const isCellEditing = isEditing && editingCell?.colName === col.name
                        return (
                          <td
                            key={col.name}
                            className={clsx('relative sticky z-10 py-1 pl-2 pr-2 transition-colors duration-700', stickyBg, isSelect && 'cursor-pointer overflow-visible hover:bg-indigo-50/50 dark:hover:bg-indigo-500/5')}
                            style={{ left: getFixedLeft(col.name) }}
                            onClick={isSelect && !isCellEditing ? () => setEditingCell({ rowId: id, colName: col.name }) : undefined}
                          >
                            {isCellEditing ? (
                              <CellSelect row={row} col={col} onCellUpdate={onCellUpdate} onNotify={onNotify} csrfToken={csrfToken} statesOptions={statesOptions} subStateNames={subStateNames} subStateComments={subStateComments} probabilitiesOptions={probabilitiesOptions} financialStagesOptions={financialStagesOptions} usersList={users} onDone={() => setEditingCell(null)} onFlash={onFlash} />
                            ) : <div className="truncate">{renderCell(cellProps(row, col))}</div>}
                            <div className="absolute inset-y-0 right-0 w-px bg-zinc-200 dark:bg-zinc-700" />
                          </td>
                        )
                      })}
                      {scrollCols.map(col => {
                        const isSelect = col.type === 'state' || col.type === 'select' || col.type === 'user_select'
                        const isCellEditing = isEditing && editingCell?.colName === col.name
                        return (
                          <td
                            key={col.name}
                            className={clsx('border-r border-zinc-100 py-1 pl-2 pr-2 dark:border-zinc-800', isSelect && 'relative cursor-pointer overflow-visible hover:bg-indigo-50/50 dark:hover:bg-indigo-500/5')}
                            onClick={isSelect && !isCellEditing ? () => setEditingCell({ rowId: id, colName: col.name }) : undefined}
                          >
                            {isCellEditing ? (
                              <CellSelect row={row} col={col} onCellUpdate={onCellUpdate} onNotify={onNotify} csrfToken={csrfToken} statesOptions={statesOptions} subStateNames={subStateNames} subStateComments={subStateComments} probabilitiesOptions={probabilitiesOptions} financialStagesOptions={financialStagesOptions} usersList={users} onDone={() => setEditingCell(null)} onFlash={onFlash} />
                            ) : <div className="truncate">{renderCell(cellProps(row, col))}</div>}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
                {paddingBottom > 0 && <tr><td colSpan={visibleColumns.length + 1} style={{ height: paddingBottom, padding: 0, border: 'none' }} /></tr>}
              </>
            )
          })()}
        </tbody>

        {/* Summary row */}
        <tfoot className="sticky bottom-0 z-20 bg-white dark:bg-zinc-900">
          <tr aria-hidden="true" style={{ height: 0, lineHeight: 0 }}>
            <td colSpan={visibleColumns.length + 1} className="bg-zinc-200 p-0 dark:bg-zinc-700" style={{ height: 1 }} />
          </tr>
          <tr className="text-[10px] font-semibold text-zinc-600 dark:text-zinc-400">
            <td className="sticky left-0 z-10 bg-white py-1.5 pl-2 pr-2 dark:bg-zinc-900">
              Σ
            </td>
            {fixedCols.map(col => (
              <td key={col.name} className="sticky z-10 bg-white py-1.5 pl-2 pr-2 dark:bg-zinc-900"
                style={{ left: getFixedLeft(col.name) }}>
              </td>
            ))}
            {scrollCols.map(col => (
              <td key={col.name} className="whitespace-nowrap py-1.5 pl-2 pr-2">
                <div className="truncate">{computeSummary(processedRows, col.name, col.type)}</div>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
      </DndContext>
    </div>
  )
}

// ─── Cell Select ───────────────────────────────
// Mounted for exactly ONE cell at a time. Auto-opens Listbox on mount.
// After selection or close → calls onDone() to unmount.

function CellSelect({ row, col, onCellUpdate, onNotify, csrfToken, statesOptions, subStateNames, subStateComments, probabilitiesOptions, financialStagesOptions, usersList, onDone, onFlash }: {
  row: Record<string, unknown>; col: DashboardColumn
  onCellUpdate: (rowId: number, updates: { col: string; val: unknown }[]) => void
  onNotify: (msg: string) => void; csrfToken: string
  statesOptions: { label: string; value: string }[]
  subStateNames: Record<string, Record<string, string>>
  subStateComments: Record<string, Record<string, string>>
  probabilitiesOptions: { label: string; value: string }[]
  financialStagesOptions: { label: string; value: string }[]
  usersList: { label: string; value: string }[]
  onDone: () => void
  onFlash: (rowId: number, status: 'success' | 'error') => void
}) {
  const humanize = (s: string | null | undefined) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  const rowId = row.id as number
  const strVal = String(row[col.name] || '')

  let options: { value: string; label: string }[] = []
  let currentValue = ''

  if (col.type === 'state') {
    options = statesOptions.map(o => ({ value: o.value, label: humanize(o.label) }))
    currentValue = statesOptions.find(o => o.label === strVal)?.value || strVal.toLowerCase().replace(/ /g, '_')
  } else if (col.type === 'select') {
    const stateKey = String(row.state || '').toLowerCase().replace(/ /g, '_')
    if (col.name === 'sub_state_name') {
      const map = subStateNames[stateKey] || {}
      options = [{ value: '', label: '—' }, ...Object.entries(map).map(([k, v]) => ({ value: k, label: humanize(v) }))]
    } else if (col.name === 'sub_state_comment') {
      const map = subStateComments[stateKey] || {}
      options = [{ value: '', label: '—' }, ...Object.entries(map).map(([k, v]) => ({ value: k, label: humanize(v) }))]
    } else if (col.name === 'probability') {
      options = [{ value: '', label: '—' }, ...probabilitiesOptions.map(o => ({ value: o.value, label: humanize(o.label) }))]
    } else if (col.name === 'finance_stage') {
      options = [{ value: '', label: '—' }, ...financialStagesOptions.map(o => ({ value: o.value, label: humanize(o.label) }))]
    }
    currentValue = options.find(o => o.label === strVal)?.value || options.find(o => o.value === strVal)?.value || strVal
  } else if (col.type === 'user_select') {
    options = [{ value: '', label: '—' }, ...usersList]
    currentValue = usersList.find(u => u.label === strVal)?.value || usersList.find(u => u.value === strVal)?.value || ''
  }

  if (options.length === 0) { onDone(); return null }

  const handleChange = async (newVal: string) => {
    onDone()
    if (newVal === currentValue) return
    const fieldName = col.type === 'user_select' ? 'assigned_to_id' : col.name
    try {
      const res = await fetch(`/inertia_customer_orders/${rowId}.json`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ customer_order: { [fieldName]: newVal } }),
      })
      if (res.ok) {
        const label = options.find(o => o.value === newVal)?.label || humanize(newVal)
        const updates: { col: string; val: unknown }[] = [{ col: col.name, val: label }]
        if (col.type === 'state') {
          if (subStateNames[newVal]) updates.push({ col: 'sub_state_name', val: '' })
          if (subStateComments[newVal]) updates.push({ col: 'sub_state_comment', val: '' })
        }
        onCellUpdate(rowId, updates)
        onFlash(rowId, 'success')
        onNotify(`Saved:${humanize(col.name)} updated`)
      } else {
        onFlash(rowId, 'error')
        onNotify(`error:Error:Failed to update ${humanize(col.name)}`)
      }
    } catch {
      onFlash(rowId, 'error')
      onNotify(`error:Error:Network error`)
    }
  }

  // Stable ref for onDone — prevents useEffect re-runs
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  // Auto-open Listbox on mount, close on Listbox dismiss
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    // Auto-open: click the Listbox button
    requestAnimationFrame(() => {
      const btn = el.querySelector('button')
      if (btn) btn.click()
    })
    // Watch for Listbox close (data-open removed) → onDone
    const observer = new MutationObserver(() => {
      if (!el.querySelector('[data-open]')) onDoneRef.current()
    })
    // Start observing after a tick (so we don't fire before it opens)
    const timer = setTimeout(() => {
      observer.observe(el, { attributes: true, attributeFilter: ['data-open'], subtree: true })
    }, 150)
    return () => { clearTimeout(timer); observer.disconnect() }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={containerRef} className="absolute inset-0 z-10 flex items-center" data-testid="cell-select" onClick={e => e.stopPropagation()}>
      <FormSelect value={currentValue} onChange={handleChange} options={options} />
    </div>
  )
}

// ─── DateRangeFilter (portal) — replaces the set-style popover on
//   date columns. Two <input type="date"> fields; either bound is
//   optional. Mirrors the legacy datePredicate.js / dateCheckBetween
//   contract — both bounds inclusive, lenient Date.parse on cell value.
function DateRangeFilter({
  colName, value, onChange, onClear, onClose, anchorButton, initialTop, initialLeft,
}: {
  colName: string
  value: { from?: string; to?: string }
  onChange: (next: { from?: string; to?: string }) => void
  onClear: () => void
  onClose: () => void
  anchorButton: HTMLButtonElement | null
  initialTop: number
  initialLeft: number
}) {
  const [from, setFrom] = useState(value.from ?? '')
  const [to,   setTo]   = useState(value.to   ?? '')
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = `dashboard-date-filter-${colName}`

  // Close on Esc / click-outside — same machinery as FilterDropdown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onClick = (e: MouseEvent) => {
      const panel = document.getElementById(panelId)
      if (panel?.contains(e.target as Node)) return
      if (anchorButton?.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    const timer = setTimeout(() => document.addEventListener('mousedown', onClick), 50)
    return () => { clearTimeout(timer); document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose, panelId, anchorButton])

  const apply = () => {
    onChange({ from: from || undefined, to: to || undefined })
    onClose()
  }
  const clear = () => {
    setFrom(''); setTo(''); onClear()
    onClose()
  }

  return createPortal(
    <div
      id={panelId}
      data-testid={`col-date-filter-panel-${colName}`}
      ref={panelRef}
      className="fixed z-[100] w-64 rounded-lg border border-zinc-200 bg-white p-3 text-xs shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
      style={{ top: initialTop, left: initialLeft }}
    >
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Date range</div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-[10px] text-zinc-500">From</span>
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            data-testid={`date-filter-from-${colName}`}
            className="mt-0.5 w-full rounded border border-zinc-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] text-zinc-500">To</span>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            data-testid={`date-filter-to-${colName}`}
            className="mt-0.5 w-full rounded border border-zinc-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          />
        </label>
      </div>
      <div className="mt-3 flex justify-between gap-2">
        <button
          type="button"
          onClick={clear}
          data-testid={`date-filter-clear-${colName}`}
          className="rounded px-2 py-1 text-[10px] font-semibold text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-700/50"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={apply}
          data-testid={`date-filter-apply-${colName}`}
          className="rounded bg-indigo-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-indigo-700"
        >
          Apply
        </button>
      </div>
    </div>,
    document.body,
  )
}

// ─── Filter Dropdown (portal — same as ProductsTable ColumnFilter) ───

function FilterDropdown({
  colName, values, selected, onToggle, onClear, onClose, anchorButton, initialTop, initialLeft,
}: {
  colName: string
  values: string[]
  selected: Set<string>
  onToggle: (col: string, val: string) => void
  onClear: () => void
  onClose: () => void
  anchorButton: HTMLButtonElement | null
  initialTop: number
  initialLeft: number
}) {
  const [search, setSearch] = useState('')
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const filtered = search ? values.filter(v => v.toLowerCase().includes(search.toLowerCase())) : values
  const isFiltered = selected.size > 0

  // Coordinates come pre-measured from the parent (captured at click
  // time from event.currentTarget). Rendering at correct position on
  // the very first frame — no flash near the logo.
  useEffect(() => {
    const id = setTimeout(() => searchRef.current?.focus(), 50)
    return () => clearTimeout(id)
  }, [])

  // Close on Escape or click outside
  const panelId = `dashboard-filter-${colName}`
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onClick = (e: MouseEvent) => {
      const panel = document.getElementById(panelId)
      if (panel?.contains(e.target as Node)) return
      if (anchorButton?.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    const timer = setTimeout(() => document.addEventListener('mousedown', onClick), 50)
    return () => { clearTimeout(timer); document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose, panelId, anchorButton])

  return createPortal(
    <div
      id={panelId}
      data-testid={`col-filter-panel-${colName}`}
      ref={panelRef}
      className="fixed z-[100] w-56 rounded-lg border border-zinc-200 bg-white text-xs shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
      style={{ top: initialTop, left: initialLeft }}
    >
      <div className="border-b border-zinc-100 p-2 dark:border-zinc-700">
        <input
          data-testid="col-filter-search"
          ref={searchRef}
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded border border-zinc-200 px-2 py-1 text-xs focus:border-indigo-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
        />
      </div>
      <div className="max-h-56 overflow-y-auto p-1.5">
        {isFiltered && (
          <button data-testid="col-filter-clear" onClick={onClear} className="mb-1 w-full rounded px-2 py-1 text-left text-xs text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-500/10">
            Clear filter
          </button>
        )}
        {filtered.length === 0 && <p data-testid="col-filter-empty" className="px-1.5 py-2 text-xs text-zinc-400">No values</p>}
        {filtered.map(v => (
          <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-700/50">
            <input
              type="checkbox"
              checked={selected.has(v)}
              onChange={() => onToggle(colName, v)}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800"
            />
            <span className="truncate">{v || '(empty)'}</span>
          </label>
        ))}
      </div>
    </div>,
    document.body,
  )
}
